import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Link } from 'react-router-dom';
import { Plus, MapPin, Calendar, DollarSign, Star, Users, Award, Crown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard() {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/bookings/my-bookings`)
      ]);

      setStats(statsRes.data);
      setBookings(bookingsRes.data);

      // Fetch properties if user is property owner
      if (user.role === 'property_owner') {
        const propertiesRes = await axios.get(`${API}/properties/owner/my-properties`);
        setProperties(propertiesRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="container">
          <div className="loading-screen">
            <div className="loading-spinner"></div>
            <p>Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  const isPropertyOwner = user.role === 'property_owner';

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">
            Welcome back, {user.full_name}!
          </h1>
          <p className="dashboard-subtitle">
            {isPropertyOwner ? 'Manage your properties and bookings' : 'Your travel dashboard'}
          </p>
          <div className="subscription-badge" style={{ marginTop: '16px' }}>
            <span className={`subscription-badge ${user.subscription_plan}`}>
              {user.subscription_plan === 'basic' ? '👑 Basic + AI Concierge' : 
               user.subscription_plan === 'ultra_plus' ? '💎 UltraPlus Member' :
               user.subscription_plan === 'super_plus' ? '⭐ SuperPlus Member' :
               user.subscription_plan === 'plus' ? '🚀 Premium Traveller' : '🎯 Traveler'}
            </span>
            {stats && (
              <Link 
                to="/loyalty" 
                style={{ 
                  marginLeft: '12px',
                  padding: '6px 12px',
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  color: 'white',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Award size={16} />
                {stats.loyalty_points} pts • {stats.loyalty_tier?.replace('_', ' ').toUpperCase()}
              </Link>
            )}
            <Link 
              to="/subscription" 
              style={{ 
                marginLeft: '8px',
                padding: '6px 12px',
                background: user.role === 'traveler' ? 'linear-gradient(135deg, #8B5CF6, #7C3AED)' : 'linear-gradient(135deg, #10B981, #059669)',
                color: 'white',
                borderRadius: '12px',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Crown size={16} />
              {user.role === 'traveler' ? 'Upgrade Points' : 'Manage Plan'}
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          {isPropertyOwner ? (
            <>
              <div className="stat-card">
                <div className="stat-value">{stats?.properties_count || 0}</div>
                <div className="stat-label">Properties Listed</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats?.total_bookings || 0}</div>
                <div className="stat-label">Total Bookings</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats?.pending_bookings || 0}</div>
                <div className="stat-label">Pending Bookings</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">${stats?.total_earnings?.toFixed(2) || '0.00'}</div>
                <div className="stat-label">Total Earnings</div>
              </div>
            </>
          ) : (
            <>
              <div className="stat-card">
                <div className="stat-value">{stats?.total_trips || 0}</div>
                <div className="stat-label">Total Trips</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats?.upcoming_trips || 0}</div>
                <div className="stat-label">Upcoming Trips</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">${stats?.total_spent?.toFixed(2) || '0.00'}</div>
                <div className="stat-label">Total Spent</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  {user.subscription_plan === 'premium' ? '🤖' : '📱'}
                </div>
                <div className="stat-label">
                  {user.subscription_plan === 'premium' ? 'AI Concierge Available' : 'Basic Features'}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '16px', 
          marginBottom: '40px',
          flexWrap: 'wrap'
        }}>
          {isPropertyOwner ? (
            <>
              <Link to="/properties/create" className="btn btn-primary">
                <Plus size={20} />
                Add New Property
              </Link>
              <Link to="/properties" className="btn btn-outline">
                <MapPin size={20} />
                Browse All Properties
              </Link>
            </>
          ) : (
            <>
              <Link to="/properties" className="btn btn-primary">
                <MapPin size={20} />
                Find Properties
              </Link>
              <Link to="/bookings" className="btn btn-outline">
                <Calendar size={20} />
                My Bookings
              </Link>
              {user.subscription_plan === 'premium' && (
                <button className="btn btn-outline" onClick={() => alert('AI Concierge coming soon!')}>
                  <Users size={20} />
                  AI Concierge
                </button>
              )}
            </>
          )}
        </div>

        {/* Recent Bookings */}
        <div style={{ 
          background: 'white', 
          borderRadius: '16px', 
          padding: '32px', 
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          marginBottom: '40px'
        }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '600', 
            marginBottom: '24px',
            color: '#2d3748'
          }}>
            Recent Bookings
          </h2>
          
          {bookings.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#718096'
            }}>
              <Calendar size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <p>No bookings yet</p>
              <p style={{ fontSize: '0.875rem' }}>
                {isPropertyOwner ? 'Your bookings will appear here once travelers book your properties.' : 'Start exploring properties to make your first booking!'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {bookings.slice(0, 5).map((booking) => (
                <div key={booking.id} style={{
                  padding: '20px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'all 0.3s ease'
                }}>
                  <div>
                    <h3 style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748' }}>
                      Booking #{booking.id.slice(-8)}
                    </h3>
                    <p style={{ color: '#718096', fontSize: '0.875rem' }}>
                      {new Date(booking.check_in_date).toLocaleDateString()} - {new Date(booking.check_out_date).toLocaleDateString()}
                    </p>
                    <p style={{ color: '#718096', fontSize: '0.875rem' }}>
                      {booking.guests} guest{booking.guests > 1 ? 's' : ''} • {booking.nights} night{booking.nights > 1 ? 's' : ''}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      background: booking.status === 'confirmed' ? '#48bb78' : 
                                  booking.status === 'pending' ? '#ed8936' : '#e53e3e',
                      color: 'white',
                      marginBottom: '8px'
                    }}>
                      {booking.status}
                    </div>
                    <p style={{ fontWeight: '600', color: '#2d3748' }}>
                      ${booking.total_amount.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Properties (for property owners) */}
        {isPropertyOwner && (
          <div style={{ 
            background: 'white', 
            borderRadius: '16px', 
            padding: '32px', 
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{ 
                fontSize: '1.5rem', 
                fontWeight: '600',
                color: '#2d3748'
              }}>
                Your Properties
              </h2>
              <Link to="/properties/create" className="btn btn-primary">
                <Plus size={20} />
                Add Property
              </Link>
            </div>
            
            {properties.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px', 
                color: '#718096'
              }}>
                <MapPin size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
                <p>No properties listed yet</p>
                <p style={{ fontSize: '0.875rem', marginBottom: '20px' }}>
                  Start earning by listing your first property on ZypeeTravel
                </p>
                <Link to="/properties/create" className="btn btn-primary">
                  List Your First Property
                </Link>
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '20px' 
              }}>
                {properties.slice(0, 4).map((property) => (
                  <div key={property.id} style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{ 
                      height: '200px', 
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '3rem'
                    }}>
                      🏠
                    </div>
                    <div style={{ padding: '20px' }}>
                      <h3 style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748' }}>
                        {property.title}
                      </h3>
                      <p style={{ color: '#718096', fontSize: '0.875rem', marginBottom: '12px' }}>
                        {property.city}, {property.state}
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600', color: '#667eea' }}>
                          ${property.price_per_night}/night
                        </span>
                        <div style={{ display: 'flex', gap: '8px', fontSize: '0.875rem', color: '#718096' }}>
                          <span>{property.bedrooms} bed</span>
                          <span>{property.bathrooms} bath</span>
                          <span>{property.max_guests} guests</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payment Information (for property owners) */}
        {isPropertyOwner && (
          <div style={{ 
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', 
            borderRadius: '16px', 
            padding: '32px', 
            border: '2px solid #10b981',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)'
          }}>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600',
              color: '#065f46',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <DollarSign size={28} />
              Payment Information
            </h2>
            <p style={{
              color: '#047857',
              fontSize: '1rem',
              lineHeight: '1.6',
              marginBottom: '20px'
            }}>
              Receive payments from travelers for your luxury resort timeshare bookings. 
              Share this secure payment link with guests for invoice-based payments.
            </p>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '16px'
            }}>
              <h3 style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#065f46',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Your Payment Link
              </h3>
              <a
                href="https://32dbb5ac-e922-4f81-85ed-b1af921ca170.paylinks.godaddy.com/0550ea6d-5fe1-492b-b893-491"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: '#10b981',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#059669'}
                onMouseOut={(e) => e.target.style.background = '#10b981'}
              >
                <DollarSign size={18} />
                Open Payment Link
              </a>
            </div>
            <div style={{
              fontSize: '0.875rem',
              color: '#065f46',
              background: 'rgba(16, 185, 129, 0.1)',
              padding: '16px',
              borderRadius: '8px',
              lineHeight: '1.5'
            }}>
              <strong>💡 Note:</strong> This payment link is connected to your FOUND Business Account and Stripe. 
              Payments processed through this link will require manual booking confirmation. For automatic processing, 
              travelers should use the Stripe checkout during booking.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;